// Phase 1 of the Mass Balance enhancement — Density & Temperature diagnostics.
// Reproduces the density check the customer has but doesn't look at: compares each grade's stated
// density (OAS Material) and implied density (movement mass/volume) against a curated per-grade
// reference band, flags out-of-range / static / mismatched values, and trends density+temp over the
// month per grade. Trucks-first (the usual mass-balance culprit). See MASS_BALANCE_ENHANCEMENT_PLAN.md.
import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, Chip, TextField, MenuItem, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Alert,
} from '@mui/material';
import { Warning, ErrorOutline, CheckCircle, LocalShipping } from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ReferenceArea,
} from 'recharts';
import { InvRow, MovRow, expectedDensity } from '../utils/massBalanceOas';

const median = (xs: number[]): number | null => {
  const a = xs.filter((v) => Number.isFinite(v)).sort((x, y) => x - y);
  return a.length ? a[Math.floor(a.length / 2)] : null;
};
const f3 = (v: number | null): string => (v === null ? '—' : v.toFixed(3));

interface Props { inv: InvRow[]; mov: MovRow[]; }

const MassBalanceDensity: React.FC<Props> = ({ inv, mov }) => {
  // ---- per-grade stated density + temperature (OAS Material) ----
  const byGrade = useMemo(() => {
    const m = new Map<string, { desc: string; dens: number[]; temp: number[]; tempSet: Set<number> }>();
    for (const r of inv) {
      const key = r.desc || r.cgid;
      const g = m.get(key) || { desc: r.desc || r.cgid, dens: [], temp: [], tempSet: new Set<number>() };
      if (r.density !== null) g.dens.push(r.density);
      if (r.temp !== null) { g.temp.push(r.temp); g.tempSet.add(Math.round(r.temp * 10) / 10); }
      m.set(key, g);
    }
    const out = Array.from(m.values()).map((g) => {
      const band = expectedDensity(g.desc);
      const medD = median(g.dens);
      const medT = median(g.temp);
      const outOfBand = band && medD !== null && (medD < band.lo || medD > band.hi);
      const staticTemp = g.temp.length >= 3 && g.tempSet.size === 1;
      return { desc: g.desc, band, medD, medT, outOfBand: !!outOfBand, staticTemp, n: g.dens.length };
    });
    // flagged first, then by name
    return out.sort((a, b) => (Number(b.outOfBand) - Number(a.outOfBand)) || (Number(b.staticTemp) - Number(a.staticTemp)) || a.desc.localeCompare(b.desc));
  }, [inv]);

  // ---- movement implied-density check (mass/volume), trucks first ----
  const movCheck = useMemo(() => {
    const m = new Map<string, { desc: string; truck: boolean; imp: number[] }>();
    for (const r of mov) {
      if (r.density === null) continue;
      const key = `${r.desc || r.cgid}|${r.stype === 'T' ? 'TRUCK' : 'PIPE'}`;
      const g = m.get(key) || { desc: r.desc || r.cgid, truck: r.stype === 'T', imp: [] };
      g.imp.push(r.density); m.set(key, g);
    }
    const out = Array.from(m.values()).map((g) => {
      const band = expectedDensity(g.desc);
      const medI = median(g.imp);
      const flag = band && medI !== null && (medI < band.lo || medI > band.hi);
      return { desc: g.desc, truck: g.truck, medI, band, n: g.imp.length, flag: !!flag };
    }).filter((r) => r.n >= 1);
    return out.sort((a, b) => (Number(b.flag) - Number(a.flag)) || (Number(b.truck) - Number(a.truck)) || (b.n - a.n));
  }, [mov]);

  // ---- density + temperature trend for a selected grade ----
  const gradeOptions = useMemo(() => byGrade.map((g) => g.desc), [byGrade]);
  const [sel, setSel] = useState<string>('');
  const selGrade = sel || (byGrade.find((g) => g.outOfBand)?.desc ?? gradeOptions[0] ?? '');
  const trend = useMemo(() => {
    const byDay = new Map<string, { d: number[]; t: number[] }>();
    for (const r of inv) {
      if ((r.desc || r.cgid) !== selGrade) continue;
      const g = byDay.get(r.dateKey) || { d: [], t: [] };
      if (r.density !== null) g.d.push(r.density);
      if (r.temp !== null) g.t.push(r.temp);
      byDay.set(r.dateKey, g);
    }
    return Array.from(byDay.entries()).sort((a, b) => a[0].localeCompare(b[0]))
      .map(([day, g]) => ({ date: day.slice(5), density: median(g.d), temp: median(g.t) }));
  }, [inv, selGrade]);
  const selBand = expectedDensity(selGrade);

  // Tank (inventory) median density for the selected grade — the value movements should match.
  const selTankDensity = useMemo(() => byGrade.find((g) => g.desc === selGrade)?.medD ?? null, [byGrade, selGrade]);
  // Individual movement shipments (the "tickets") for the selected grade, with deviation vs tank.
  // When real BOL tickets are ingested they'll feed in here too; for now these are the OAS Ext rows.
  const selShipments = useMemo(() => {
    const band = expectedDensity(selGrade);
    return mov.filter((r) => (r.desc || r.cgid) === selGrade && (r.vol > 0 || r.density !== null))
      .map((r) => {
        const dev = (r.density !== null && selTankDensity !== null) ? r.density - selTankDensity : null;
        const offBand = band && r.density !== null && (r.density < band.lo || r.density > band.hi);
        const off = (dev !== null && Math.abs(dev) > 0.02) || !!offBand;
        return { r, dev, off };
      })
      .sort((a, b) => Math.abs(b.dev ?? 0) - Math.abs(a.dev ?? 0));
  }, [mov, selGrade, selTankDensity]);
  const nShipOff = selShipments.filter((s) => s.off).length;

  const nOut = byGrade.filter((g) => g.outOfBand).length;
  const nStatic = byGrade.filter((g) => g.staticTemp).length;
  const nMovFlag = movCheck.filter((r) => r.flag).length;

  const kpi = (label: string, value: string, color: string) => (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 130 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color, mt: 0.25 }}>{value}</Typography>
    </Paper>
  );

  if (!inv.length && !mov.length) {
    return <Alert severity="info">Upload the Material Download (for stated density &amp; temperature) and/or Ext Movement (for implied movement density) to run the density diagnostics.</Alert>;
  }

  return (
    <Box>
      <Alert severity="info" icon={<Warning />} sx={{ mb: 2 }}>
        Density is the #1 mass-balance troubleshooting lever. This compares each grade&apos;s <strong>stated</strong> density
        (Material Download) and <strong>implied</strong> density (movement mass ÷ volume) against a reference band, and flags
        out-of-range / static / mismatched values — <strong>trucks first</strong>. Reference bands are a seeded starting point;
        the yield engineer will tune them on-site.
      </Alert>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {kpi('Grades checked', String(byGrade.length), 'text.primary')}
        {kpi('Density out-of-band', String(nOut), nOut ? 'error.main' : 'success.main')}
        {kpi('Static temperature', String(nStatic), nStatic ? 'warning.main' : 'success.main')}
        {kpi('Movement anomalies', String(nMovFlag), nMovFlag ? 'error.main' : 'success.main')}
      </Stack>

      {/* Density + temperature trend for a selected grade */}
      {gradeOptions.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">Density &amp; temperature trend</Typography>
            <Box sx={{ flex: 1 }} />
            <TextField select size="small" label="Grade" value={selGrade} onChange={(e) => setSel(e.target.value)} sx={{ minWidth: 260 }}>
              {gradeOptions.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </TextField>
          </Stack>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={trend} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="d" domain={['auto', 'auto']} tick={{ fontSize: 11 }} tickFormatter={(v) => Number(v).toFixed(3)} width={60} />
              <YAxis yAxisId="t" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v)}°`} />
              {selBand && <ReferenceArea yAxisId="d" y1={selBand.lo} y2={selBand.hi} fill="#2e7d32" fillOpacity={0.08} />}
              <RTooltip contentStyle={{ fontSize: 12 }} formatter={(v: any, n: any) => (n === 'Temp (°C)' ? `${Number(v).toFixed(1)}°C` : Number(v).toFixed(4))} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="d" dataKey="density" name="Density (kg/L)" stroke="#1f4e79" strokeWidth={2} dot={{ r: 2 }} connectNulls />
              <Line yAxisId="t" dataKey="temp" name="Temp (°C)" stroke="#e67e22" strokeWidth={2} dot={{ r: 2 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
          <Typography variant="caption" color="text.secondary">
            Shaded band = expected density range{selBand ? ` (${selBand.lo}–${selBand.hi} kg/L)` : ' (no reference for this grade)'}. Flat density with a static temperature often means a defaulted/stale reading.
          </Typography>
        </Paper>
      )}

      {/* Grade drill-down: individual movement shipments vs the tank density ("do they match?") */}
      {selGrade && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">Movement shipments for {selGrade}</Typography>
            <Typography variant="caption" color="text.secondary">
              tank density {selTankDensity !== null ? `${selTankDensity.toFixed(3)} kg/L` : '—'}
              {selShipments.length ? ` · ${selShipments.length} shipment${selShipments.length > 1 ? 's' : ''}` : ''}
              {nShipOff ? ` · ${nShipOff} off` : ''}
            </Typography>
          </Stack>
          {selShipments.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No movement rows for this grade in the uploaded Ext Movement file.</Typography>
          ) : (
            <Table size="small" sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Dir</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Volume (m³)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Density (kg/L)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>vs tank</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Temp</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Match</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selShipments.map(({ r, dev, off }, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{r.dateKey || '—'}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {r.stype === 'T' ? <LocalShipping sx={{ fontSize: 15, color: 'warning.main' }} /> : null}
                        <span>{r.stype === 'T' ? 'Truck' : 'Pipe'}</span>
                      </Stack>
                    </TableCell>
                    <TableCell>{r.ind === 'R' ? 'In' : r.ind === 'D' ? 'Out' : '—'}</TableCell>
                    <TableCell align="right">{r.vol ? Math.round(r.vol).toLocaleString() : '—'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: off ? 700 : 400, color: off ? 'error.main' : 'text.primary' }}>{f3(r.density)}</TableCell>
                    <TableCell align="right" sx={{ color: dev === null ? 'text.secondary' : (off ? 'error.main' : 'success.main') }}>{dev === null ? '—' : `${dev >= 0 ? '+' : ''}${dev.toFixed(3)}`}</TableCell>
                    <TableCell align="right">{r.temp === null ? '—' : `${r.temp.toFixed(1)}°`}</TableCell>
                    <TableCell>{off ? <Chip size="small" color="error" icon={<ErrorOutline />} label="Off" /> : <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Each shipment&apos;s density (mass ÷ volume) should match the tank density above — a large &quot;vs tank&quot; gap is the diagnostic. When BOL tickets are ingested they&apos;ll appear here too.
          </Typography>
        </Paper>
      )}

      {/* Stated density by grade */}
      {byGrade.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Stated density by grade (Material Download) — click a grade to chart &amp; drill it</Typography>
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Density (med)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Expected</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Temp (med)</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Flags</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byGrade.map((g) => (
                <TableRow key={g.desc} hover selected={g.desc === selGrade} onClick={() => setSel(g.desc)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{g.desc}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: g.outOfBand ? 700 : 400, color: g.outOfBand ? 'error.main' : 'text.primary' }}>{f3(g.medD)}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{g.band ? `${g.band.lo}–${g.band.hi}` : '—'}</TableCell>
                  <TableCell align="right">{g.medT === null ? '—' : `${g.medT.toFixed(1)}°`}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {g.outOfBand && <Tooltip title="Stated density is outside the expected range for this product"><Chip size="small" color="error" icon={<ErrorOutline />} label="Density OOR" /></Tooltip>}
                      {g.staticTemp && <Tooltip title="Temperature never changes across snapshots — likely a defaulted / stale reading"><Chip size="small" color="warning" icon={<Warning />} label="Static temp" /></Tooltip>}
                      {!g.outOfBand && !g.staticTemp && <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Movement implied-density check, trucks first */}
      {movCheck.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Movement density check — implied ρ = mass ÷ volume (trucks first)</Typography>
          <Table size="small" sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Implied ρ (med)</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Expected</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>n</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Flag</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movCheck.map((r, i) => (
                <TableRow key={`${r.desc}-${r.truck}-${i}`} hover selected={r.desc === selGrade} onClick={() => setSel(r.desc)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{r.desc}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {r.truck ? <LocalShipping sx={{ fontSize: 16, color: 'warning.main' }} /> : null}
                      <span>{r.truck ? 'Truck' : 'Pipe'}</span>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: r.flag ? 700 : 400, color: r.flag ? 'error.main' : 'text.primary' }}>{f3(r.medI)}</TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>{r.band ? `${r.band.lo}–${r.band.hi}` : '—'}</TableCell>
                  <TableCell align="right">{r.n}</TableCell>
                  <TableCell>{r.flag ? <Chip size="small" color="error" icon={<ErrorOutline />} label="Out of range" /> : <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            The Ext Movement file has no density column, so ρ is implied from mass ÷ volume. Grades without a reference band show no flag.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MassBalanceDensity;
